import { ErrorCode } from './errorCode'

type Json =
    | string
    | number
    | boolean
    | null
    | { [key: string]: Json }
    | Json[]

function toSafeJsonValue(value: unknown, depth = 0, seen = new WeakSet<object>()): Json {
    if (value === null) return null

    if (typeof value === 'string')
        return value.length > 10_000 ? `${value.slice(0, 10_000)}…(truncated)` : value
    if (typeof value === 'number' || typeof value === 'boolean') return value
    if (typeof value === 'bigint') return value.toString()
    if (typeof value === 'undefined') return 'undefined'
    if (typeof value === 'symbol') return value.toString()
    if (typeof value === 'function') return `[function ${value.name || 'anonymous'}]`

    if (value instanceof Date) return value.toISOString()
    if (value instanceof Error) {
        const cause = (value as { cause?: unknown }).cause
        return {
            name: value.name,
            message: value.message,
            ...(cause !== undefined ? { cause: toSafeJsonValue(cause, depth + 1, seen) } : {}),
        }
    }

    if (Array.isArray(value)) {
        if (depth >= 5) return '[array depth limit reached]'
        return value.slice(0, 50).map((v) => toSafeJsonValue(v, depth + 1, seen))
    }

    if (typeof value === 'object') {
        if (depth >= 5) return '[object depth limit reached]'

        const obj = value as Record<string, unknown>
        if (seen.has(obj)) return '[circular]'
        seen.add(obj)

        const out: Record<string, Json> = {}
        const keys = Object.keys(obj).slice(0, 50)
        for (const k of keys) out[k] = toSafeJsonValue(obj[k], depth + 1, seen)
        return out
    }

    return String(value)
}

export function logError(code: ErrorCode, error: unknown, context?: Record<string, unknown>) {
    const payload = {
        level: 'error',
        code,
        timestamp: new Date().toISOString(),
        runtime: typeof window === 'undefined' ? 'server' : 'browser',
        error: toSafeJsonValue(error),
        ...(context ? { context: toSafeJsonValue(context) } : {}),
    }

    const isDev = process.env.NODE_ENV !== 'production'
    console.error(isDev ? JSON.stringify(payload, null, 2) : JSON.stringify(payload))
}